/**
 * chatNotification.ts
 * WhatsApp-like chat notification utilities:
 *  - playNotificationSound()   – synthesised ding via Web Audio API (no file needed)
 *  - showBrowserNotification() – OS-level popup when tab is unfocused
 *  - requestNotificationPermission() – call on explicit user action
 */

/** Play a short two-tone WhatsApp-like notification sound */
export function playNotificationSound(): void {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();

    const tone = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.28, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      osc.start(start);
      osc.stop(start + duration + 0.05);
    };

    const t = ctx.currentTime;
    tone(587, t, 0.14);        // D5 — first ding
    tone(784, t + 0.17, 0.22); // G5 — second ding (higher)
  } catch {
    // silently ignore — audio not critical
  }
}

/** Ask the browser for notification permission (call from a user-gesture handler) */
export async function requestNotificationPermission(): Promise<void> {
  if (!("Notification" in window)) return;
  if (Notification.permission === "default") {
    await Notification.requestPermission();
  }
}

/**
 * Show an OS-level browser notification only when the tab is not focused.
 * Silently skips if permission not granted.
 */
export function showBrowserNotification(title: string, body: string): void {
  if (!("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (document.visibilityState === "visible") return; // skip when tab is active

  const n = new Notification(title, {
    body,
    icon: "/favicon.ico",
    badge: "/favicon.ico",
  });

  n.onclick = () => {
    window.focus();
    n.close();
  };

  setTimeout(() => n.close(), 6000);
}
